/* global Office */
import { useState, useCallback, useRef, useEffect } from "react";
import LeftNav from "./LeftNav";
import SecurityPage from "./SecurityPage";
import PasswordsPage from "./PasswordsPage";
import ZugriffsverwaltungPage from "./ZugriffsverwaltungPage";
import { useDialogAuth } from "../../hooks/useDialogAuth";
import { sendRequest, cleanup as cleanupBridge } from "../../services/dialogBridge";
import { OPS } from "../../services/messageOps";
import DevConsolePanel from "../DevConsolePanel";
import { useI18n } from "../../contexts/I18nContext";
import LanguageSelector from "../common/LanguageSelector";

/**
 * Password Manager Main Dialog
 * Opens in Office Dialog window.
 * All crypto state lives in the Sidebar (Crypto Host).
 * This dialog communicates with the sidebar via the bridge (dialogBridge).
 */
export default function PasswordManagerDialog() {
  const { t } = useI18n();
  const [activePage, setActivePage] = useState("security");

  // UI state – populated from sidebar via bridge GET_STATE
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwortDatenbankId, setPasswortDatenbankId] = useState(null);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [masterKey, setMasterKey] = useState(null); // Kept for child component props (PasswordsPage, etc.)
  const [masterKeyVersion, setMasterKeyVersion] = useState(1);
  const [isPaid, setIsPaid] = useState(false);
  const [isOwner, setIsOwner] = useState(true);
  const [showDeactivationDialog, setShowDeactivationDialog] = useState(false);
  const [countdown, setCountdown] = useState(7);
  const focusSectionRef = useRef(null);

  // Trigger the deactivation dialog with countdown (reused for 409 conflict handling)
  const handleConflictDetected = useCallback(() => {
    setShowDeactivationDialog(true);
    setCountdown(7);
  }, []);

  // Navigate to a page, optionally with a focus hint
  const handleNavigate = useCallback((pageId, options) => {
    if (options?.focusSection) {
      focusSectionRef.current = options.focusSection;
    } else if (options?.focusDbId) {
      focusSectionRef.current = { type: "focus-db", dbId: options.focusDbId };
    } else {
      focusSectionRef.current = null;
    }
    setActivePage(pageId);
  }, []);

  // Authenticate dialog with session exchange
  const { isAuthenticated, oid, error, loading } = useDialogAuth();

  /**
   * Fetches current crypto state from sidebar via bridge.
   */
  const syncStateFromSidebar = useCallback(async () => {
    try {
      const state = await sendRequest(OPS.GET_STATE);
      if (state) {
        setIsUnlocked(state.isUnlocked || false);
        setPasswortDatenbankId(state.passwortDatenbankId || null);
        setMasterKeyVersion(state.masterKeyVersion || 1);
        setIsOwner(state.isOwner !== false);
        setActiveUserCount(state.activeUserCount || 0);
        setIsPaid(state.isPaid || false);

        // If sidebar is already unlocked, fetch MasterKey bytes for dialog pages
        if (state.isUnlocked && state.passwortDatenbankId) {
          try {
            const mkResult = await sendRequest(OPS.DECRYPT_MASTER_KEY);
            if (mkResult?.masterKeyBase64) {
              const mkBytes = Uint8Array.from(atob(mkResult.masterKeyBase64), c => c.charCodeAt(0));
              setMasterKey(mkBytes);
            }
          } catch (mkErr) {
            console.warn("[PasswordManagerDialog] Failed to get MasterKey from sidebar:", mkErr);
          }
        }
      }
    } catch (err) {
      console.warn("[PasswordManagerDialog] Failed to sync state from sidebar:", err);
    }
  }, []);

  /**
   * Re-fetches state from sidebar after a database change.
   * The sidebar's CryptoContext handles the actual refresh logic.
   */
  const refreshAfterDbChange = useCallback(async () => {
    // Tell sidebar to refresh, then sync our UI state
    // The sidebar's refreshAfterDbChange handles fallback DB logic
    await syncStateFromSidebar();

    // Check if PM was deactivated
    try {
      const state = await sendRequest(OPS.GET_STATE);
      if (state && !state.isUnlocked && !state.passwortDatenbankId) {
        // Possible auto-deactivation
        setIsUnlocked(false);
        setPasswortDatenbankId(null);
        setMasterKey(null);
        setActiveUserCount(0);
        setIsOwner(true);
        setActivePage("security");
        setShowDeactivationDialog(true);
        setCountdown(7);
      }
    } catch (err) {
      console.warn("[PasswordManagerDialog] Error checking state after DB change:", err);
    }
  }, [syncStateFromSidebar]);

  // Sync state from sidebar on mount (after authentication)
  useEffect(() => {
    if (isAuthenticated && oid) {
      syncStateFromSidebar();
    }
  }, [isAuthenticated, oid, syncStateFromSidebar]);

  // Cleanup bridge on unmount
  useEffect(() => {
    return () => cleanupBridge();
  }, []);

  // Auto-close dialog after countdown
  useEffect(() => {
    if (showDeactivationDialog && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showDeactivationDialog && countdown === 0) {
      closeDialog();
    }
  }, [showDeactivationDialog, countdown]);

  // Helper function to close the dialog
  const closeDialog = () => {
    try {
      if (typeof Office !== "undefined" && Office.context?.ui) {
        Office.context.ui.messageParent(
          JSON.stringify({ type: "pm-deactivated" })
        );
      } else {
        window.close();
      }
    } catch (msgErr) {
      console.error("[PasswordManagerDialog] messageParent failed:", msgErr);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh"
      }}>
        <div>
          <p>{t("Authentifizierung läuft...")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !isAuthenticated) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        padding: "20px"
      }}>
        <div style={{
          backgroundColor: "#fff3cd",
          padding: "20px",
          borderRadius: "8px",
          border: "1px solid #ffc107"
        }}>
          <h3 style={{ margin: "0 0 10px 0", color: "#856404" }}>
            {t("Authentifizierung fehlgeschlagen")}
          </h3>
          <p style={{ margin: 0, color: "#856404" }}>
            {error || t("Sitzung ist ungültig oder abgelaufen")}
          </p>
          <p style={{ margin: "10px 0 0 0", fontSize: "14px", color: "#666" }}>
            {t("Bitte schließen Sie dieses Fenster und versuchen Sie es erneut.")}
          </p>
        </div>
      </div>
    );
  }

  // Authenticated - render main dialog
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      {/* Left Navigation */}
      <LeftNav
        activePage={activePage}
        onNavigate={handleNavigate}
        isUnlocked={isUnlocked}
      />

      {/* Content Area */}
      <div style={{ flex: 1, padding: "20px", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
          <LanguageSelector />
        </div>
        {activePage === "security" && (
          <SecurityPage
            oid={oid}
            isUnlockedFromParent={isUnlocked}
            onNavigate={handleNavigate}
            passwortDatenbankId={passwortDatenbankId}
            masterKey={masterKey}
            masterKeyVersion={masterKeyVersion}
            isOwner={isOwner}
            onUnlockSuccess={async (dbId, userCount, decryptedMasterKey, paidStatus, ownerStatus, mkVersion) => {
              // Unlock was performed via bridge – sync state from sidebar
              setIsUnlocked(true);
              setPasswortDatenbankId(dbId);
              setActiveUserCount(userCount);
              setMasterKey(decryptedMasterKey);
              setIsPaid(paidStatus || false);
              setIsOwner(ownerStatus !== false);
              setMasterKeyVersion(mkVersion || 1);
            }}
            onMasterKeyRotated={(newMk, newVersion) => {
              setMasterKey(newMk);
              setMasterKeyVersion(newVersion);
            }}
            onConflictDetected={handleConflictDetected}
          />
        )}

        {activePage === "passwords" && (
          <PasswordsPage
            oid={oid}
            passwortDatenbankId={passwortDatenbankId}
            initialActiveUserCount={activeUserCount}
            masterKey={masterKey}
            isPaid={isPaid}
            isOwner={isOwner}
            onNavigate={handleNavigate}
            onDatabaseDeleted={refreshAfterDbChange}
            onAccessEnded={refreshAfterDbChange}
          />
        )}

        {activePage === "access" && (
          <ZugriffsverwaltungPage
            oid={oid}
            passwortDatenbankId={passwortDatenbankId}
            masterKey={masterKey}
            masterKeyVersion={masterKeyVersion}
            isPaid={isPaid}
            onNavigate={handleNavigate}
            focusSectionRef={focusSectionRef}
            onConflictDetected={handleConflictDetected}
            onActiveDbChanged={(newDbId, newMasterKey, newUserCount, newIsOwner) => {
              setPasswortDatenbankId(newDbId);
              if (newMasterKey) {
                setMasterKey(newMasterKey);
              }
              if (newUserCount !== undefined) {
                setActiveUserCount(newUserCount);
              }
              if (newIsOwner !== undefined) {
                setIsOwner(newIsOwner);
              }
            }}
          />
        )}
      </div>

      {/* Dev-only: log panel on the right */}
      {process.env.NODE_ENV === "development" && (
        <DevConsolePanel variant="dialog" />
      )}

      {/* Close Dialog Overlay (deactivation or 409 conflict) */}
      {showDeactivationDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <span style={{ fontSize: "24px", marginRight: "12px" }}>ℹ️</span>
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Sitzung beendet
              </h3>
            </div>

            {/* Message */}
            <div
              style={{
                color: "#555",
                lineHeight: "1.6",
              }}
            >
              <p style={{ margin: "0 0 12px 0" }}>
                Die Passwortdatenbank wurde aktualisiert oder ist nicht mehr verfügbar. Bitte öffnen Sie den Passwort-Manager erneut.
              </p>
              <p style={{ margin: "0 0 12px 0" }}>
                Der Passwort-Manager wird in{" "}
                <strong style={{ color: "#dc3545", fontSize: "16px" }}>
                  {countdown}
                </strong>{" "}
                Sekunden geschlossen.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
